// src/screens/InviteCodeScreen.js
// ============================================================================
// INVITE CODE GATE - Users must enter a valid invite code before signup.
// Validates the code against Cloud Functions, then forwards to SignUpWizard.
//
// Includes a "Sign Up for Beta" modal so users without a code can request
// access. Submissions are stored in Firestore (beta_signups) and emailed
// to support@critcash.com via a Cloud Function.
// ============================================================================

import React, { useState } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Modal,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { httpsCallable, getFunctions } from 'firebase/functions';

export default function InviteCodeScreen({ navigation }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [spotsInfo, setSpotsInfo] = useState(null);

  // Beta signup modal state
  const [showSignupModal, setShowSignupModal] = useState(false);
  const [signupName, setSignupName] = useState('');
  const [signupEmail, setSignupEmail] = useState('');
  const [signupGames, setSignupGames] = useState('');
  const [signupLoading, setSignupLoading] = useState(false);
  const [signupError, setSignupError] = useState('');
  const [signupSuccess, setSignupSuccess] = useState(false);

  const handleValidate = async () => {
    const trimmed = code.trim();
    if (!trimmed) {
      setError('Please enter an invite code.');
      return;
    }

    setLoading(true);
    setError('');
    setSpotsInfo(null);

    try {
      const validateFn = httpsCallable(getFunctions(), 'validateInviteCode');
      const result = await validateFn({ code: trimmed });

      if (result.data.valid) {
        setSpotsInfo(`${result.data.spotsRemaining} spots remaining`);
        // Navigate to signup with the validated code
        navigation.navigate('SignUp', { inviteCode: result.data.code });
      } else {
        setError(result.data.reason || 'Invalid code.');
      }
    } catch (err) {
      setError('Something went wrong. Try again.');
      console.error('Invite code validation error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle code input — auto-format generated 12-char codes (XXXX-XXXX-XXXX),
  // but accept custom codes (special chars or longer than 12 chars) as-is.
  const handleCodeChange = (text) => {
    const upper = text.toUpperCase();

    // Special characters present — strip any auto-inserted hyphens so codes
    // like "GDC2026!" don't get corrupted to "GDC2-026!".
    if (!/^[A-Z0-9-]*$/.test(upper)) {
      setCode(upper.replace(/-/g, '').trim());
      setError('');
      return;
    }

    const stripped = upper.replace(/-/g, '');

    // Longer than a generated 12-char code — preserve the user's input
    // verbatim instead of truncating (fixes codes like "CRITCASH-FOUNDER").
    if (stripped.length > 12) {
      setCode(upper);
      setError('');
      return;
    }

    // Auto-format as XXXX-XXXX-XXXX for generated codes.
    let clean;
    if (stripped.length <= 4) {
      clean = stripped;
    } else if (stripped.length <= 8) {
      clean = stripped.slice(0, 4) + '-' + stripped.slice(4);
    } else {
      clean = stripped.slice(0, 4) + '-' + stripped.slice(4, 8) + '-' + stripped.slice(8);
    }
    setCode(clean);
    setError('');
  };

  // ============================================================================
  // Beta Signup Modal Handlers
  // ============================================================================

  const resetSignupModal = () => {
    setSignupName('');
    setSignupEmail('');
    setSignupGames('');
    setSignupError('');
    setSignupSuccess(false);
    setSignupLoading(false);
  };

  const handleOpenSignup = () => {
    resetSignupModal();
    setShowSignupModal(true);
  };

  const handleCloseSignup = () => {
    setShowSignupModal(false);
    resetSignupModal();
  };

  const handleBetaSignup = async () => {
    const name = signupName.trim();
    const email = signupEmail.trim().toLowerCase();
    const games = signupGames.trim();

    // Validation
    if (!name) {
      setSignupError('Please enter your name.');
      return;
    }
    if (!email) {
      setSignupError('Please enter your email.');
      return;
    }
    // Basic email format check
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setSignupError('Please enter a valid email address.');
      return;
    }

    setSignupLoading(true);
    setSignupError('');

    try {
      const signupFn = httpsCallable(getFunctions(), 'submitBetaSignup');
      const result = await signupFn({
        name,
        email,
        games: games || 'Not specified',
      });

      if (result.data.success) {
        setSignupSuccess(true);
      } else {
        setSignupError(result.data.reason || 'Something went wrong. Try again.');
      }
    } catch (err) {
      console.error('Beta signup error:', err);
      if (err?.code === 'functions/already-exists') {
        setSignupError('This email has already been registered for the beta.');
      } else {
        setSignupError('Something went wrong. Please try again.');
      }
    } finally {
      setSignupLoading(false);
    }
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <View style={styles.content}>
        {/* Header */}
        <View style={styles.header}>
          <Ionicons name="lock-closed" size={48} color="#FCD000" />
          <Text style={styles.title}>CritCash Closed Beta</Text>
          <Text style={styles.subtitle}>
            Enter your invite code to create an account.
          </Text>
        </View>

        {/* Code Input */}
        <View style={styles.inputContainer}>
          <TextInput
            style={[styles.input, error ? styles.inputError : null]}
            value={code}
            onChangeText={handleCodeChange}
            placeholder="Enter invite code"
            placeholderTextColor="#555"
            autoCapitalize="characters"
            autoCorrect={false}
            maxLength={30}
            returnKeyType="go"
            onSubmitEditing={handleValidate}
          />
        </View>

        {/* Error Message */}
        {error ? (
          <View style={styles.feedbackRow}>
            <Ionicons name="alert-circle" size={16} color="#ef4444" />
            <Text style={styles.errorText}>{error}</Text>
          </View>
        ) : null}

        {/* Spots remaining info */}
        {spotsInfo ? (
          <View style={styles.feedbackRow}>
            <Ionicons name="checkmark-circle" size={16} color="#22c55e" />
            <Text style={styles.successText}>{spotsInfo}</Text>
          </View>
        ) : null}

        {/* Submit Button */}
        <TouchableOpacity
          style={[styles.button, loading && styles.buttonDisabled]}
          onPress={handleValidate}
          disabled={loading}
        >
          {loading ? (
            <ActivityIndicator color="#000" />
          ) : (
            <Text style={styles.buttonText}>Continue</Text>
          )}
        </TouchableOpacity>

        {/* Beta Signup CTA */}
        <TouchableOpacity
          style={styles.betaSignupLink}
          onPress={handleOpenSignup}
        >
          <Ionicons name="mail-outline" size={16} color="#FCD000" />
          <Text style={styles.betaSignupText}>
            Don't have a code?{' '}
            <Text style={styles.betaSignupBold}>Sign up for the beta</Text>
          </Text>
        </TouchableOpacity>

        {/* Already have an account */}
        <TouchableOpacity
          style={styles.loginLink}
          onPress={() => navigation.navigate('Login')}
        >
          <Text style={styles.linkText}>
            Already have an account?{' '}
            <Text style={styles.linkTextBold}>Log in</Text>
          </Text>
        </TouchableOpacity>

        {/* Back to landing */}
        <TouchableOpacity
          style={styles.backLink}
          onPress={() => navigation.navigate('Landing')}
        >
          <Ionicons name="arrow-back" size={16} color="#666" />
          <Text style={styles.backText}>Back</Text>
        </TouchableOpacity>
      </View>

      {/* ================================================================== */}
      {/* BETA SIGNUP MODAL                                                  */}
      {/* ================================================================== */}
      <Modal
        visible={showSignupModal}
        transparent
        animationType="fade"
        onRequestClose={handleCloseSignup}
      >
        <View style={styles.modalOverlay}>
          <KeyboardAvoidingView
            behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
            style={styles.modalKeyboardView}
          >
            <View style={styles.modalContainer}>
              {/* Close button */}
              <TouchableOpacity
                style={styles.modalClose}
                onPress={handleCloseSignup}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
              >
                <Ionicons name="close" size={24} color="#AAAAAA" />
              </TouchableOpacity>

              {signupSuccess ? (
                /* ---- Success State ---- */
                <View style={styles.successContainer}>
                  <Ionicons name="checkmark-circle" size={56} color="#22c55e" />
                  <Text style={styles.successTitle}>You're on the list!</Text>
                  <Text style={styles.successMessage}>
                    We'll reach out when a spot opens up. Keep an eye on your inbox.
                  </Text>
                  <TouchableOpacity
                    style={styles.successButton}
                    onPress={handleCloseSignup}
                  >
                    <Text style={styles.successButtonText}>Got it</Text>
                  </TouchableOpacity>
                </View>
              ) : (
                /* ---- Signup Form ---- */
                <>
                    <View style={styles.modalHeader}>
                      <Ionicons name="people-outline" size={28} color="#FCD000" />
                      <Text style={styles.modalTitle}>Join the Beta</Text>
                      <Text style={styles.modalSubtitle}>
                        Sign up to get notified when a spot opens up.
                      </Text>
                    </View>

                    {/* Name */}
                    <Text style={styles.fieldLabel}>Name</Text>
                    <TextInput
                      style={styles.modalInput}
                      value={signupName}
                      onChangeText={(t) => { setSignupName(t); setSignupError(''); }}
                      placeholder="Your name"
                      placeholderTextColor="#555"
                      autoCapitalize="words"
                      autoCorrect={false}
                    />

                    {/* Email */}
                    <Text style={styles.fieldLabel}>Email</Text>
                    <TextInput
                      style={styles.modalInput}
                      value={signupEmail}
                      onChangeText={(t) => { setSignupEmail(t); setSignupError(''); }}
                      placeholder="you@example.com"
                      placeholderTextColor="#555"
                      keyboardType="email-address"
                      autoCapitalize="none"
                      autoCorrect={false}
                    />

                    {/* Games (optional) */}
                    <Text style={styles.fieldLabel}>
                      What games do you play?{' '}
                      <Text style={styles.fieldOptional}>(optional)</Text>
                    </Text>
                    <TextInput
                      style={styles.modalInput}
                      value={signupGames}
                      onChangeText={(t) => { setSignupGames(t); setSignupError(''); }}
                      placeholder="e.g. Street Fighter 6, Tekken 8..."
                      placeholderTextColor="#555"
                      autoCorrect={false}
                    />

                    {/* Error */}
                    {signupError ? (
                      <View style={styles.modalErrorRow}>
                        <Ionicons name="alert-circle" size={14} color="#ef4444" />
                        <Text style={styles.modalErrorText}>{signupError}</Text>
                      </View>
                    ) : null}

                  {/* Submit */}
                  <TouchableOpacity
                    style={[styles.modalButton, signupLoading && styles.buttonDisabled]}
                    onPress={handleBetaSignup}
                    disabled={signupLoading}
                  >
                    {signupLoading ? (
                      <ActivityIndicator color="#000" />
                    ) : (
                      <Text style={styles.modalButtonText}>Sign Up</Text>
                    )}
                  </TouchableOpacity>
                </>
              )}
            </View>
          </KeyboardAvoidingView>
        </View>
      </Modal>
    </KeyboardAvoidingView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#000000',
  },
  content: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: 32,
  },
  header: {
    alignItems: 'center',
    marginBottom: 40,
  },
  title: {
    color: '#FCD000',
    fontSize: 28,
    fontWeight: '800',
    marginTop: 16,
  },
  subtitle: {
    color: '#AAAAAA',
    fontSize: 16,
    marginTop: 8,
    textAlign: 'center',
  },
  inputContainer: {
    width: '100%',
    maxWidth: 320,
    marginBottom: 16,
  },
  input: {
    backgroundColor: '#111111',
    borderWidth: 1,
    borderColor: '#222222',
    borderRadius: 8,
    color: '#FFFFFF',
    fontSize: 24,
    fontWeight: '600',
    textAlign: 'center',
    paddingVertical: 16,
    paddingHorizontal: 16,
    letterSpacing: 3,
  },
  inputError: {
    borderColor: '#ef4444',
  },
  feedbackRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 6,
  },
  errorText: {
    color: '#ef4444',
    fontSize: 14,
  },
  successText: {
    color: '#22c55e',
    fontSize: 14,
  },
  button: {
    backgroundColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 14,
    paddingHorizontal: 48,
    width: '100%',
    maxWidth: 320,
    alignItems: 'center',
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: '#000000',
    fontSize: 18,
    fontWeight: '800',
  },

  // Beta signup link
  betaSignupLink: {
    marginTop: 24,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  betaSignupText: {
    color: '#AAAAAA',
    fontSize: 14,
  },
  betaSignupBold: {
    color: '#FCD000',
    fontWeight: '600',
  },

  loginLink: {
    marginTop: 16,
  },
  linkText: {
    color: '#AAAAAA',
    fontSize: 14,
  },
  linkTextBold: {
    color: '#FCD000',
    fontWeight: '600',
  },
  backLink: {
    marginTop: 16,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  backText: {
    color: '#666',
    fontSize: 14,
  },

  // ============================================================================
  // Modal Styles
  // ============================================================================
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalKeyboardView: {
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
  },
  modalContainer: {
    backgroundColor: '#111111',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#222222',
    paddingHorizontal: 24,
    paddingTop: 20,
    paddingBottom: 24,
    width: '90%',
    maxWidth: 380,
  },
  modalClose: {
    position: 'absolute',
    top: 10,
    right: 10,
    zIndex: 10,
  },
  modalHeader: {
    alignItems: 'center',
    marginBottom: 16,
  },
  modalTitle: {
    color: '#FCD000',
    fontSize: 20,
    fontWeight: '800',
    marginTop: 8,
  },
  modalSubtitle: {
    color: '#AAAAAA',
    fontSize: 13,
    marginTop: 4,
    textAlign: 'center',
    lineHeight: 18,
  },
  fieldLabel: {
    color: '#FFFFFF',
    fontSize: 13,
    fontWeight: '600',
    marginBottom: 5,
    marginTop: 10,
  },
  fieldOptional: {
    color: '#666',
    fontWeight: '400',
  },
  modalInput: {
    backgroundColor: '#000000',
    borderWidth: 1,
    borderColor: '#222222',
    borderRadius: 8,
    color: '#FFFFFF',
    fontSize: 15,
    paddingVertical: 10,
    paddingHorizontal: 14,
  },
  modalErrorRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: 10,
  },
  modalErrorText: {
    color: '#ef4444',
    fontSize: 13,
  },
  modalButton: {
    backgroundColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 13,
    alignItems: 'center',
    marginTop: 16,
  },
  modalButtonText: {
    color: '#000000',
    fontSize: 16,
    fontWeight: '800',
  },

  // Success state
  successContainer: {
    alignItems: 'center',
    paddingVertical: 20,
  },
  successTitle: {
    color: '#FFFFFF',
    fontSize: 22,
    fontWeight: '800',
    marginTop: 16,
  },
  successMessage: {
    color: '#AAAAAA',
    fontSize: 14,
    textAlign: 'center',
    marginTop: 8,
    lineHeight: 20,
  },
  successButton: {
    backgroundColor: '#FCD000',
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 40,
    marginTop: 24,
  },
  successButtonText: {
    color: '#000000',
    fontSize: 16,
    fontWeight: '800',
  },
});